import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import { useEffect, useState } from 'react'

import ActiveTabs from '../../components/ActiveTabs'
import { getTranslate } from '../../localization'
import { EntityList } from '../../types/entityList'
import {
  GetRoleFeaturesInput,
  RoleFeature,
  UpdateFeatureFlagInput
} from '../../types/featureFlag'
import { EntityType } from '../../types/reserveMonitoringList'
import { RoleBrief } from '../../types/role'
import useStyles from './styles/filters.styles'

type Props = {
  autoCompleteForRole: any
  setAutoCompleteForRele: React.Dispatch<any>
  autoCompleteForEntity: any
  setAutoCompleteForEntity: React.Dispatch<any>
  roleFeaturesInput: GetRoleFeaturesInput
  setRoleFeaturesInput: React.Dispatch<
    React.SetStateAction<GetRoleFeaturesInput>
  >
  updateFeatureFlagInput: UpdateFeatureFlagInput
  setUpdateFeatureFlagInput: React.Dispatch<
    React.SetStateAction<UpdateFeatureFlagInput>
  >
  roles: RoleBrief[]
  entityList: EntityList[]
  temporaryFeatures: RoleFeature[]
  setFilteredFeatures: React.Dispatch<React.SetStateAction<RoleFeature[]>>
}

export default function FeatureFilters({
  autoCompleteForRole,
  setAutoCompleteForRele,
  autoCompleteForEntity,
  setAutoCompleteForEntity,
  roleFeaturesInput,
  setRoleFeaturesInput,
  updateFeatureFlagInput,
  setUpdateFeatureFlagInput,
  roles,
  entityList,
  temporaryFeatures,
  setFilteredFeatures
}: Props) {
  const classes = useStyles()
  const [activeStatus, setActiveStatus] = useState('All')
  const [searchInputVal, setSearchInputVal] = useState('')

  useEffect(() => {
    let searchfilteredFeatures = searchInputVal
      ? matchSorter(temporaryFeatures, searchInputVal, {
          keys: ['featureGroup', 'features.*.name']
        })
      : temporaryFeatures

    if (activeStatus !== 'All') {
      searchfilteredFeatures = searchfilteredFeatures
        .map((roleFeature) => ({
          ...roleFeature,
          features: roleFeature.features.filter((f) =>
            activeStatus === 'Active' ? f.selected : !f.selected
          )
        }))
        .filter((rf) => rf.features.length > 0)
    }

    setFilteredFeatures(searchfilteredFeatures)
  }, [searchInputVal, temporaryFeatures, activeStatus])

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <AutocompleteWithLabel
          options={
            roles.length > 0
              ? roles.map((role) => ({
                  label: role.name,
                  id: role.roleID,
                  value: (role.entityType as EntityType) || ''
                }))
              : []
          }
          inputSize="small"
          getOptionLabel={(option) => option.label || ''}
          value={autoCompleteForRole}
          onChange={(_, value: any) => {
            if (value) {
              setAutoCompleteForRele({
                value: value.value,
                id: value.id,
                label: value.label
              })

              setFilteredFeatures([])
              setRoleFeaturesInput((prev) => {
                if (value.value) {
                  return {
                    ...prev,
                    roleID: value.id,
                    entityType: value.value,
                    entityCode: undefined
                  }
                }

                return {
                  ...prev,
                  roleID: value.id,
                  entityType: undefined,
                  entityCode: undefined
                }
              })
              setUpdateFeatureFlagInput({
                ...updateFeatureFlagInput,
                roleID: value.id
              })
            } else {
              setFilteredFeatures([])
              setAutoCompleteForRele(null)
              setAutoCompleteForEntity(null)
              setRoleFeaturesInput({ roleID: '' })
              setUpdateFeatureFlagInput({
                roleID: '',
                featureIDs: [],
                entityCode: undefined
              })
            }
          }}
          label={getTranslate('Role')}
        />
        {autoCompleteForRole && autoCompleteForRole.value && (
          <InputWithLabel
            label={getTranslate('Entity Type')}
            fullWidth
            inputSize="small"
            value={(autoCompleteForRole && autoCompleteForRole.value) || ''}
            disabled
          />
        )}
        {autoCompleteForRole && autoCompleteForRole.value && (
          <AutocompleteWithLabel
            options={
              entityList
                ? entityList.map((entity) => ({
                    label: entity.label,
                    value: entity.entityCode
                  }))
                : []
            }
            inputSize="small"
            getOptionLabel={(option) => option.label || ''}
            value={autoCompleteForEntity}
            onChange={(_, value: any) => {
              if (value) {
                setAutoCompleteForEntity({
                  value: value.value,
                  label: value.label
                })
                if (roleFeaturesInput.roleID) {
                  setRoleFeaturesInput((prev) => ({
                    ...prev,
                    entityCode: value.value
                  }))
                }
                if (updateFeatureFlagInput.roleID) {
                  setUpdateFeatureFlagInput({
                    ...updateFeatureFlagInput,
                    entityCode: value.value
                  })
                }
              } else {
                setAutoCompleteForEntity(null)
              }
            }}
            label={getTranslate('Entity')}
          />
        )}
      </div>
      <div className={classes.row2}>
        <InputWithLabel
          label={getTranslate('Features')}
          placeholder={getTranslate('Search in Features')}
          value={searchInputVal}
          onChange={(event) => setSearchInputVal(event.currentTarget.value)}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          inputSize="small"
        />
      </div>
      <ActiveTabs
        title="Feature Status"
        value={activeStatus}
        setValue={setActiveStatus}
      />
    </div>
  )
}
